import React from 'react';
import { Briefcase, Building2, Users2, Award } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Badge } from './ui/badge';
import { administrativeServices } from '../data/cvData';

const Administrative = () => {
  const { iitPalakkad, iitGuwahati } = administrativeServices;

  return (
    <section id="administrative" className="section-container bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50 relative overflow-hidden">
      {/* Background decorations */}
      <div className="absolute top-20 left-20 w-96 h-96 bg-gradient-to-br from-blue-200 to-purple-200 rounded-full filter blur-3xl opacity-20 animate-float"></div>
      
      <h2 className="section-title">Administrative Services</h2>
      
      <div className="w-full relative z-10">
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          <Card className="border-2 border-transparent hover:border-blue-200 overflow-hidden group hover-lift">
            <div className="h-2 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
            <CardContent className="p-8 text-center bg-gradient-to-br from-blue-50 to-cyan-50 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-600 to-cyan-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
              <Building2 size={48} className="text-blue-600 mx-auto mb-4 relative z-10" />
              <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2 relative z-10">IIT Palakkad</h3>
              <div className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2 relative z-10">{iitPalakkad.length}</div>
              <p className="text-slate-600 relative z-10">Administrative Roles</p>
            </CardContent>
          </Card>
          <Card className="border-2 border-transparent hover:border-purple-200 overflow-hidden group hover-lift">
            <div className="h-2 bg-gradient-to-r from-purple-600 to-pink-500"></div>
            <CardContent className="p-8 text-center bg-gradient-to-br from-purple-50 to-pink-50 relative">
              <div className="absolute inset-0 bg-gradient-to-br from-purple-600 to-pink-500 opacity-0 group-hover:opacity-10 transition-opacity duration-500"></div>
              <Award size={48} className="text-purple-600 mx-auto mb-4 relative z-10" />
              <h3 className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 relative z-10">IIT Guwahati</h3>
              <div className="text-5xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2 relative z-10">{iitGuwahati.length}</div>
              <p className="text-slate-600 relative z-10">Administrative Roles</p>
            </CardContent>
          </Card>
        </div>

        <Tabs defaultValue="palakkad" className="w-full">
          <TabsList className="grid w-full grid-cols-2 mb-8 bg-white shadow-lg">
            <TabsTrigger value="palakkad" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-600 data-[state=active]:to-cyan-500 data-[state=active]:text-white">
              IIT Palakkad (Current)
            </TabsTrigger>
            <TabsTrigger value="guwahati" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-600 data-[state=active]:to-pink-500 data-[state=active]:text-white">
              IIT Guwahati (2017-2023)
            </TabsTrigger>
          </TabsList>

          <TabsContent value="palakkad">
            <div className="grid gap-4">
              {iitPalakkad.map((service, index) => (
                <Card key={index} className="hover-lift border-2 border-transparent hover:border-blue-200 interactive-card">
                  <div className="h-2 bg-gradient-to-r from-blue-600 to-cyan-500"></div>
                  <CardHeader className="bg-gradient-to-br from-white to-blue-50">
                    <CardTitle className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="bg-gradient-to-br from-blue-600 to-cyan-500 p-3 rounded-lg">
                          <Briefcase size={24} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold bg-gradient-to-r from-blue-600 to-cyan-600 bg-clip-text text-transparent mb-2">
                            {service.role}
                          </h3>
                          <p className="text-sm text-slate-600 font-normal">{service.department}</p>
                        </div>
                      </div>
                      <Badge className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white hover:from-blue-600 hover:to-cyan-600">
                        {service.year}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="guwahati">
            <div className="grid gap-4">
              {iitGuwahati.map((service, index) => (
                <Card key={index} className="hover-lift border-2 border-transparent hover:border-purple-200 interactive-card">
                  <div className="h-2 bg-gradient-to-r from-purple-600 to-pink-500"></div>
                  <CardHeader className="bg-gradient-to-br from-white to-purple-50">
                    <CardTitle className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex items-start gap-3 flex-1">
                        <div className="bg-gradient-to-br from-purple-600 to-pink-500 p-3 rounded-lg">
                          <Users2 size={24} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent mb-2">
                            {service.role}
                          </h3>
                          <p className="text-sm text-slate-600 font-normal">{service.department}</p>
                        </div>
                      </div>
                      <Badge className="bg-gradient-to-r from-purple-500 to-pink-500 text-white hover:from-purple-600 hover:to-pink-600">
                        {service.year}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                </Card>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="mt-12 bg-gradient-to-br from-white to-blue-50 rounded-3xl p-8 shadow-xl border-2 border-blue-100">
          <div className="flex items-center gap-3 mb-4">
            <Award size={32} className="text-blue-600" />
            <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Notable Contributions
            </h3>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white p-4 rounded-lg border-l-4 border-blue-500">
              <h4 className="font-bold text-slate-800 mb-2">Centre for Medical Device Innovation (CMDI)</h4>
              <p className="text-slate-600 text-sm">Founder Member & Co-convener at IIT Guwahati</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-l-4 border-purple-500">
              <h4 className="font-bold text-slate-800 mb-2">Lab Establishment</h4>
              <p className="text-slate-600 text-sm">Established full-fledged composite manufacturing and testing labs at both IIT Guwahati and IIT Palakkad</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-l-4 border-cyan-500">
              <h4 className="font-bold text-slate-800 mb-2">Research Conclaves</h4>
              <p className="text-slate-600 text-sm">Convener for Research Conclave'20 and Research & Industry Conclave 2022</p>
            </div>
            <div className="bg-white p-4 rounded-lg border-l-4 border-pink-500">
              <h4 className="font-bold text-slate-800 mb-2">Academia-Industry Bridge</h4>
              <p className="text-slate-600 text-sm">Faculty In-Charge of Academia-Industry Collaboration at IIT Palakkad</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Administrative;
